import { TemplatePreview } from "@/components/slides/list/TemplatePreview";
import type { SavedSection } from "@/lib/hooks/sectionPresets/useSectionPresets";
import { GENERATABLE_TEMPLATES, getTemplateLabel } from "@/lib/slides/slideTemplates";
import type { SlideTemplate } from "@ai-teach/types";
import { isFreeformTemplate, resolveSlideTemplate } from "@ai-teach/types";
import { Button, Modal } from "@ai-teach/ui-web";
import { useState } from "react";
import { FaCheck } from "react-icons/fa";
import { HiArrowLeft } from "react-icons/hi";
import type { BookScope } from "./BookScopePicker";
import { ReferenceSlidePicker } from "./ReferenceSlidePicker";
import { SavedSectionBar } from "./SavedSectionBar";
import { SourcePicker } from "./SourcePicker";
import { SubslideParentPicker } from "./SubslideParentPicker";
import {
  DATA_SOURCES,
  DATA_SOURCE_ORDER,
  countedItem,
  countsAsCap,
  isCountableSource,
  resolveSlideCount,
} from "./countRules";
import { TEMPLATE_EXPLANATIONS } from "./templateExplanations";
import type {
  BookLessonRef,
  ContextLines,
  DataSource,
  GeneratorSection,
  PartLines,
  ReferenceSlide,
  SlideCount,
  SubslideParent,
} from "./types";

const DEFAULT_PARENT: SubslideParent = { template: "passageReading", tagId: "", each: 2 };

/** Every answer the slide marks, wherever it sits: a choice element, an odd-one-out, a blank's options. */
function countAnswers(value: unknown): number {
  if (Array.isArray(value)) return value.reduce((sum, one) => sum + countAnswers(one), 0);
  if (typeof value !== "object" || value === null) return 0;
  return (
    ("isCorrectAnswer" in value ? 1 : 0) +
    Object.values(value).reduce((sum: number, one) => sum + countAnswers(one), 0)
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <span className="label block">{label}</span>
      {hint && <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
      {children}
    </div>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2.5 rounded-xl border border-gray-200 px-4 py-3 dark:border-gray-800">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 accent-accent-500"
      />
      <span className="min-w-0">
        <span className="label block">{label}</span>
        <span className="text-xs text-gray-500 dark:text-gray-400">{hint}</span>
      </span>
    </label>
  );
}

interface SectionEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (section: GeneratorSection) => void;
  /** The lesson whose slides a subslide section can attach to; absent where no lesson exists yet. */
  lessonId?: string;
  /** Lets a subslide section be written with no lesson to attach to, which only costs the preview. */
  allowSubslides?: boolean;
  /** Off where the caller stores the section itself, so the bar would be a second way to save it. */
  hideSavedSections?: boolean;
  /** Only the counts need it — a section can be written before a book lesson is picked. */
  targetBook: BookLessonRef | null;
  /** The book whose sections this section may scope itself to. */
  bookScope: BookScope;
  /** Which lesson's lines stand for the book content where a batch has no one target lesson. */
  sampleLesson?: { id: string; printedLabel: string } | null;
  /** Off where there is no one lesson whose lines could be edited, as in a batch or a preset. */
  canEditLines?: boolean;
  section?: GeneratorSection;
}

export function SectionEditorModal({
  isOpen,
  onClose,
  onSave,
  lessonId,
  allowSubslides,
  hideSavedSections,
  targetBook,
  bookScope,
  sampleLesson,
  canEditLines,
  section,
}: SectionEditorModalProps) {
  const [name, setName] = useState(section?.name ?? "");
  const [slideName, setSlideName] = useState(section?.slideName ?? "");
  const [numberSlides, setNumberSlides] = useState(section?.numberSlides ?? false);
  const [savedId, setSavedId] = useState<string | null>(section?.sectionPresetId ?? null);
  const [template, setTemplate] = useState<SlideTemplate | null>(section?.template ?? null);
  const [reference, setReference] = useState<ReferenceSlide | null>(
    section?.referenceSlide ?? null
  );
  const [context, setContext] = useState<DataSource[]>(section?.context ?? ["words"]);
  const [scopes, setScopes] = useState<string[]>(section?.sectionScopes ?? []);
  const [subLists, setSubLists] = useState<string[]>(section?.subLists ?? []);
  const [contextLines, setContextLines] = useState<ContextLines[]>(section?.contextLines ?? []);
  const [partLines, setPartLines] = useState<PartLines[]>(section?.partLines ?? []);
  const [count, setCount] = useState<SlideCount>(
    section?.count ?? { rule: "perSourceItem", countFrom: "words", each: 1 }
  );
  const [prompt, setPrompt] = useState(section?.prompt ?? "");
  const [randomizeChoices, setRandomizeChoices] = useState(
    section?.randomizeChoicePositions ?? true
  );
  const [generateSentence, setGenerateSentence] = useState(section?.generateSentence ?? false);
  // Without a lesson there are no main slides to attach to, unless the caller writes the rule alone.
  const canSubslide = lessonId !== undefined || allowSubslides === true;
  const [parent, setParent] = useState<SubslideParent | null>(
    canSubslide ? (section?.parent ?? null) : null
  );

  // Unchecking the sub-list a count names would leave it counting nothing, so the count falls back
  // to the main word list rather than silently resolving to no slides.
  const chooseSubLists = (next: string[]) => {
    setSubLists(next);
    if (count.rule === "perSourceItem" && !isCountableSource(count.countFrom, next)) {
      setCount({ ...count, countFrom: "words" });
    }
  };

  const resolved = resolveSlideCount(count, targetBook, contextLines);
  const capped = countsAsCap(template);
  const slideNamePreview = slideName.trim() || (template ? getTemplateLabel(template) : "Slide");
  const writesSentence = template === "vocabulary";
  const nothingChecked =
    !parent && context.length === 0 && scopes.length === 0 && subLists.length === 0;
  // Only a freeform canvas lays its choices out as elements; a fixed layout has no slots to swap.
  // One lone answer has nowhere to move to either, so there is nothing to offer.
  const hasChoiceSlots =
    isFreeformTemplate(resolveSlideTemplate(reference?.slide)) &&
    countAnswers(reference?.slide.elements ?? []) > 1;
  const subslideTemplates = parent
    ? GENERATABLE_TEMPLATES.filter((one) => one !== "conversation")
    : GENERATABLE_TEMPLATES;

  // The slides are named after their template until the author writes a name of their own.
  const chooseTemplate = (picked: SlideTemplate) => {
    setTemplate(picked);
    // No source counts the sounds a lesson teaches, so a capped template is given a ceiling instead.
    if (countsAsCap(picked) && count.rule === "perSourceItem")
      setCount({ rule: "fixed", total: 8 });
    if (slideName.trim() === "" || (template && slideName === getTemplateLabel(template))) {
      setSlideName(getTemplateLabel(picked));
    }
  };

  // Picking a reference before a template is the common path, so the reference names the template
  // rather than being filtered by one the author has not chosen yet.
  const selectReference = (picked: ReferenceSlide) => {
    setReference(picked);
    chooseTemplate(picked.template);
  };

  const applySaved = (saved: SavedSection) => {
    setSavedId(saved.id);
    setName(saved.name);
    setTemplate(saved.template);
    setSlideName(getTemplateLabel(saved.template));
    setNumberSlides(false);
    setReference(saved.referenceSlide);
    setContext(saved.context);
    setScopes(saved.sectionScopes ?? []);
    setSubLists(saved.subLists ?? []);
    if (saved.count) setCount(saved.count);
    setParent(canSubslide ? (saved.parent ?? null) : null);
    setPrompt(saved.prompt);
    setRandomizeChoices(saved.randomizeChoicePositions);
    setGenerateSentence(saved.generateSentence);
  };

  const toggleContext = (source: DataSource) => {
    setContext((prev) =>
      prev.includes(source) ? prev.filter((one) => one !== source) : [...prev, source]
    );
  };

  // Null restores the book's own lines for that source.
  const changeLines = (source: DataSource, lines: string[] | null) => {
    setContextLines((prev) => {
      const rest = prev.filter((one) => one.source !== source);
      return lines === null ? rest : [...rest, { source, lines }];
    });
  };

  // Null restores what the book prints under that part.
  const changePartLines = (part: string, lines: string[] | null) => {
    setPartLines((prev) => {
      const rest = prev.filter((one) => one.section !== part);
      return lines === null ? rest : [...rest, { section: part, lines }];
    });
  };

  const save = () => {
    if (!template || !reference) return;
    onSave({
      id: section?.id ?? crypto.randomUUID(),
      ...(name.trim() === "" ? {} : { name: name.trim() }),
      slideName: slideName.trim() || getTemplateLabel(template),
      numberSlides,
      ...(savedId ? { sectionPresetId: savedId } : {}),
      template,
      referenceSlide: reference,
      context: parent ? [] : context,
      ...(parent || scopes.length === 0 ? {} : { sectionScopes: scopes }),
      ...(parent || subLists.length === 0 ? {} : { subLists }),
      ...(parent || contextLines.length === 0 ? {} : { contextLines }),
      ...(parent || partLines.length === 0 ? {} : { partLines }),
      ...(parent ? { parent } : { count }),
      prompt: prompt.trim(),
      randomizeChoicePositions: hasChoiceSlots && randomizeChoices,
      generateSentence: writesSentence && generateSentence,
    });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={section ? "Edit section" : "Add section"}
      size="xl"
    >
      <div className="space-y-6">
        {/* Sticking above the scrollport and padding the slack back in leaves the modal's own top
            padding no gap for the content to show through. */}
        <div className="sticky -top-4 z-10 -mx-6 -mt-4 flex items-center gap-3 border-b border-gray-200 bg-surface px-6 pb-3 pt-7 dark:border-gray-800">
          <button
            type="button"
            onClick={onClose}
            className="-ml-1.5 flex items-center gap-1 rounded-lg px-1.5 py-1 text-sm text-gray-500 transition-colors hover:bg-black/5 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-100"
          >
            <HiArrowLeft className="h-4 w-4" />
            Back
          </button>

          <div className="ml-auto flex items-center gap-2">
            {nothingChecked && (
              <span className="hidden text-xs text-warning-500 sm:block">
                Nothing checked — written from the example slide alone.
              </span>
            )}
            <Button variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={!template || !reference || parent?.tagId === ""}
            >
              {section ? "Save section" : "Add section"}
            </Button>
          </div>
        </div>

        {!hideSavedSections && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-background px-4 py-3 dark:border-gray-800">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Start from a saved section
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Reuses a template, reference slide, book content and count you saved before.
              </p>
            </div>
            <SavedSectionBar
              current={
                template && reference
                  ? {
                      template,
                      referenceSlideId: reference.id,
                      context: parent ? [] : context,
                      ...(parent || scopes.length === 0 ? {} : { sectionScopes: scopes }),
                      ...(parent || subLists.length === 0 ? {} : { subLists }),
                      ...(parent ? { parent } : { count }),
                      prompt: prompt.trim(),
                      randomizeChoicePositions: hasChoiceSlots && randomizeChoices,
                      generateSentence: writesSentence && generateSentence,
                    }
                  : null
              }
              currentName={name}
              appliedId={savedId}
              onAppliedIdChange={setSavedId}
              onUse={applySaved}
            />
          </div>
        )}

        {canSubslide && (
          <Toggle
            label="These are subslides"
            hint="Written from a main slide in this lesson, not from the book, and played with it."
            checked={parent !== null}
            onChange={(next) => {
              setParent(next ? (section?.parent ?? DEFAULT_PARENT) : null);
              if (next && template === "conversation") setTemplate(null);
            }}
          />
        )}

        {(hasChoiceSlots || writesSentence) && (
          <div className="grid gap-3 sm:grid-cols-2">
            {hasChoiceSlots && (
              <Toggle
                label="Randomize answer position"
                hint="Every slide moves its answers off the spots the reference slide puts them in."
                checked={randomizeChoices}
                onChange={setRandomizeChoices}
              />
            )}
            {writesSentence && (
              <Toggle
                label="Write the example sentence"
                hint="Each card gets a sentence written for this lesson."
                checked={generateSentence}
                onChange={setGenerateSentence}
              />
            )}
          </div>
        )}

        <div className="space-y-2">
          <label htmlFor="section-name" className="label block">
            Name <span className="font-normal text-gray-500">(optional)</span>
          </label>
          <input
            id="section-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={template ? getTemplateLabel(template) : "What this section adds"}
            className="w-full rounded-lg border border-gray-200 bg-background px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-gray-700"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="slide-name" className="label block">
            Slide name
          </label>
          <div className="flex flex-wrap items-center gap-3">
            <input
              id="slide-name"
              value={slideName}
              onChange={(event) => setSlideName(event.target.value)}
              placeholder={slideNamePreview}
              className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-background px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-gray-700"
            />
            <label className="flex shrink-0 items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <input
                type="checkbox"
                checked={numberSlides}
                onChange={(event) => setNumberSlides(event.target.checked)}
                className="h-4 w-4 accent-accent-500"
              />
              Use number
            </label>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Every slide of this section is called{" "}
            <span className="font-medium text-gray-700 dark:text-gray-200">
              {numberSlides ? `${slideNamePreview} 1, ${slideNamePreview} 2…` : slideNamePreview}
            </span>
          </p>
        </div>

        <Field label="Template">
          <div className="grid grid-cols-[repeat(auto-fill,minmax(152px,1fr))] gap-2">
            {subslideTemplates.map((value) => {
              const isSelected = template === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => chooseTemplate(value)}
                  className={`group relative flex flex-col items-center gap-2 rounded-2xl p-2 ring-1 transition-all ${
                    isSelected
                      ? "bg-accent-500/10 ring-2 ring-accent-500"
                      : "ring-transparent hover:bg-gray-100 dark:hover:bg-white/5"
                  }`}
                >
                  <TemplatePreview template={value} />
                  <span
                    className={`caption flex h-8 items-center text-center font-medium leading-tight ${
                      isSelected ? "text-accent-500" : "text-secondary-200"
                    }`}
                  >
                    {getTemplateLabel(value)}
                  </span>
                  {isSelected && (
                    <div className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-accent-500 shadow">
                      <FaCheck className="text-white" fontSize={10} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          {template && (
            <p className="rounded-lg bg-black/5 px-3 py-2 text-xs text-gray-600 dark:bg-white/5 dark:text-gray-300">
              {TEMPLATE_EXPLANATIONS[template]}
            </p>
          )}
        </Field>

        <Field
          label="Reference slide"
          hint="Every generated slide keeps this one's layout, coordinates and styling, and the generator is shown it as an example of what you want. Its tags and domains are copied too."
        >
          <ReferenceSlidePicker selected={reference} onSelect={selectReference} />
        </Field>

        {parent ? (
          <Field
            label="Main slides to attach to"
            hint="Every slide of this lesson with this template and tag gets its own subslides, written from what that slide says."
          >
            <SubslideParentPicker
              {...(lessonId ? { lessonId } : {})}
              value={parent}
              onChange={setParent}
            />
          </Field>
        ) : (
          <Field
            label="Book content to use"
            hint="Which parts of the lesson the generator may draw on, and only those. A checked part is read whole, so its lines need no kind checked alongside them; words and grammar points come from the whole lesson either way, since the source labels printed lines only. Lines you change in a row are this section's own, and never written back to the book."
          >
            <SourcePicker
              book={targetBook}
              bookScope={bookScope}
              sampleLesson={sampleLesson ?? null}
              lines={contextLines}
              partLines={partLines}
              selected={context}
              scopes={scopes}
              subLists={subLists}
              onToggle={toggleContext}
              onScopesChange={setScopes}
              onSubListsChange={chooseSubLists}
              {...(canEditLines
                ? { onLinesChange: changeLines, onPartLinesChange: changePartLines }
                : {})}
            />
          </Field>
        )}

        {!parent && (
          <Field label={capped ? "How many slides at most" : "How many slides"}>
            <div className="flex flex-wrap items-center gap-2">
              <select
                aria-label="Slide count rule"
                value={count.rule}
                onChange={(event) =>
                  setCount(
                    event.target.value === "fixed"
                      ? { rule: "fixed", total: 1 }
                      : { rule: "perSourceItem", countFrom: context[0] ?? "words", each: 1 }
                  )
                }
                className="rounded-lg border border-gray-200 bg-background px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-gray-700"
              >
                <option value="perSourceItem">Per item</option>
                <option value="fixed">Fixed number</option>
              </select>

              <input
                type="number"
                min={1}
                aria-label={
                  count.rule === "fixed"
                    ? capped
                      ? "Most slides to write"
                      : "Number of slides"
                    : "Slides per item"
                }
                value={count.rule === "fixed" ? count.total : count.each}
                onChange={(event) => {
                  const next = Math.max(1, Number(event.target.value));
                  setCount(
                    count.rule === "fixed"
                      ? { rule: "fixed", total: next }
                      : { ...count, each: next }
                  );
                }}
                className="w-20 rounded-lg border border-gray-200 bg-background px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-gray-700"
              />

              {count.rule === "perSourceItem" && (
                <select
                  aria-label="Counted per"
                  value={count.countFrom}
                  onChange={(event) => setCount({ ...count, countFrom: event.target.value })}
                  className="rounded-lg border border-gray-200 bg-background px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-gray-700"
                >
                  {DATA_SOURCE_ORDER.map((source) => (
                    <option key={source} value={source}>
                      per {DATA_SOURCES[source].item}
                    </option>
                  ))}
                  {/* A sub-list is a source of its own, so a section can be counted per one of its
                      words — four Real life words, four slides. */}
                  {subLists.map((name) => (
                    <option key={name} value={name}>
                      per {countedItem(name)}
                    </option>
                  ))}
                </select>
              )}

              <span className="text-sm text-gray-500 dark:text-gray-400">
                {resolved === null
                  ? "Pick a book lesson to see the total"
                  : capped
                    ? `→ up to ${resolved} slide${resolved === 1 ? "" : "s"}, fewer where the lesson teaches fewer sounds`
                    : `→ ${resolved} slide${resolved === 1 ? "" : "s"}`}
              </span>
            </div>
          </Field>
        )}

        <div className="space-y-2">
          <label htmlFor="section-prompt" className="label block">
            Fine-tune prompt <span className="font-normal text-gray-500">(optional)</span>
          </label>
          <textarea
            id="section-prompt"
            rows={14}
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder="e.g. Write a fresh natural sentence rather than reusing the book's example. The two wrong options are other words from this lesson, in the same form as the answer."
            className="w-full resize-y rounded-lg border border-gray-200 bg-background px-3 py-2 text-sm leading-relaxed focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-gray-700"
          />
        </div>
      </div>
    </Modal>
  );
}
